import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { RefreshGrantModel } from '../interfaces/auth/refresh-model';
import { ProfileModel } from '../interfaces/auth/profile-model';
import { AuthTokenModel } from '../interfaces/auth/auth-tokens-model';
import { RegisterModel } from '../interfaces/auth/register-model';
import { LoginModel } from '../interfaces/auth/login-model';
import { Observable, throwError } from 'rxjs';
import { finalize, catchError } from 'rxjs/operators';
import { APIAuthTokenModel } from '../interfaces/auth/api-auth-tokens-model';

interface APIKeyResponse {
  apiKey: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  profile: ProfileModel;

  constructor(private http: HttpClient) {}

  register(data: RegisterModel): Observable<HttpResponse<any>> {
    return this.http.post('account/register', data, { observe: 'response' });
  }

  getBookingToolAPIKey() {
    return this.http.get<APIKeyResponse>('api/order-quote/APIKey').toPromise();
  }

  login(user: LoginModel): Observable<HttpResponse<any>> {
    return this.getTokens(user, 'password').pipe(
      catchError((res) => throwError(res.json())),
      finalize(() => this.scheduleRefresh()),
    );
  }

  refreshTokens(): Observable<any> {
    return this.getTokens(
      { refresh_token: this.retrieveTokens().refresh_token },
      'refresh_token',
    ).pipe(catchError((res) => throwError(res.json())));
  }

  public testAccess(): Promise<string> {
    return this.http
      .get('api/auth/testAccess', {
        responseType: 'text',
        headers: new HttpHeaders({
          Authorization: 'bearer ' + this.retrieveTokens().access_token,
        }),
      })
      .toPromise()
      .then((data) => {
        console.log('testAccess data: ', data);
        return data;
      });
  }

  public testAccess2(): Promise<string> {
    return this.http
      .get('api/auth/testAccess2', {
        responseType: 'text',
        headers: new HttpHeaders({
          //'Authorization': 'bearer ' + this.retrieveTokens().access_token
        }),
      })
      .toPromise()
      .then((data) => {
        return data;
      });
  }

  private storeToken(tokens: AuthTokenModel): void {
    localStorage.setItem('auth-tokens', JSON.stringify(tokens));
  }

  private retrieveTokens(): AuthTokenModel {
    const tokensString = localStorage.getItem('auth-tokens');
    const tokensModel: AuthTokenModel = tokensString == null ? null : JSON.parse(tokensString);
    return tokensModel;
  }

  private removeToken(): void {
    localStorage.removeItem('auth-tokens');
  }

  private storeAPIToken(key: string, token: APIAuthTokenModel): void {
    localStorage.setItem('api-key-token', token.token);
    localStorage.setItem('api-key', key);
  }

  retrieveAPIToken(): APIAuthTokenModel {
    const tokensString = localStorage.getItem('api-key-token');
    const tokensModel: APIAuthTokenModel = tokensString == null ? null : JSON.parse(tokensString);

    if (tokensModel != null && tokensModel.expiration_date > new Date().getTime().toString()) {
      console.log('USING EXISTING TOKEN');
      return tokensModel;
    }

    const apiKey = localStorage.getItem('api-key');

    console.log('REFRESHING TOKEN: APIKEY ', apiKey);

    // If the apiKey has expired, fetch a new one
    this.refreshAPIAuthToken(apiKey).then((x) => {
      console.log('REFRESHED TOKEN AND CAME BACK WITH ' + x);
      return x;
    });
  }

  refreshAPIAuthToken(apiKey: string) {
    let headers = { 'X-API-KEY': apiKey };

    return this.http
      .post<APIAuthTokenModel>('v1/authenticate', {}, { headers: headers })
      .toPromise()
      .then((token) => {
        // Expires in 100 seconds
        token.expiration_date = new Date(new Date().getTime() + 100 * 1000).getTime().toString();
        this.storeAPIToken(apiKey, token);
      })
      .catch();
  }

  private getTokens(
    data: RefreshGrantModel | LoginModel,
    grantType: string,
  ): Observable<HttpResponse<any>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    Object.assign(data, { grant_type: grantType, scope: 'openid offline_access' });

    const params = new URLSearchParams();
    Object.keys(data).forEach((key) => params.append(key, data[key]));

    this.http
      .post<AuthTokenModel>('api/auth/token', params.toString(), { headers: headers })
      .toPromise()
      .then((tokens) => {
        const now = new Date();
        tokens.expiration_date = new Date(now.getTime() + tokens.expires_in * 1000)
          .getTime()
          .toString();

        //const profile: ProfileModel = jwtDecode(tokens.id_token);

        this.storeToken(tokens);
      })
      .catch();

    return null;
  }

  private scheduleRefresh(): void {
    //this.refreshSubscription$ = this.tokens$
    //    .first()
    //    // refresh every half the total expiration time
    //    .flatMap(tokens => Observable.interval(tokens.expires_in / 2 * 1000))
    //    .flatMap(() => this.refreshTokens())
    //    .subscribe();
  }
}
